import { useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ChevronUp, MessageSquare } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { MessageBubble } from './message-bubble';
import type { Message } from '@/lib/types/messaging';

// Helper to format date for separators
const formatDateSeparator = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const isToday = date.toDateString() === today.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) return "Aujourd'hui";
  if (isYesterday) return 'Hier';

  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
};

// Get date key for grouping
const getDateKey = (dateString: string) => {
  return new Date(dateString).toDateString();
};

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  onImageClick?: (imageUrl: string) => void;
  onAudioPlay?: (audioUrl: string) => void;
}

export function MessageList({
  messages,
  isLoading = false,
  isLoadingMore = false,
  hasNextPage = false,
  onLoadMore,
  onImageClick,
  onAudioPlay,
}: MessageListProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (containerRef.current && isFirstLoad.current && messages.length > 0) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      isFirstLoad.current = false;
    }
  }, [messages]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current || !hasNextPage || isLoadingMore) return;

    const { scrollTop } = containerRef.current;
    if (scrollTop < 100 && onLoadMore) {
      onLoadMore();
    }
  }, [hasNextPage, isLoadingMore, onLoadMore]);

  // Group messages by date for separators (must be before early returns)
  const messagesWithSeparators = useMemo(() => {
    const result: Array<{ type: 'message'; message: Message } | { type: 'separator'; date: string }> = [];
    let lastDateKey = '';

    // Messages are in reverse order (newest first due to flex-col-reverse)
    // We need to process them in chronological order to add separators correctly
    const chronologicalMessages = [...messages].reverse();

    chronologicalMessages.forEach((message) => {
      const dateKey = getDateKey(message.createdAt);
      if (dateKey !== lastDateKey) {
        result.push({ type: 'separator', date: message.createdAt });
        lastDateKey = dateKey;
      }
      result.push({ type: 'message', message });
    });

    // Reverse back to display newest first
    return result.reverse();
  }, [messages]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="size-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
          <MessageSquare className="size-8 text-muted-foreground/50" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">
          {t('messaging.noMessages', 'Aucun message pour le moment')}
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1 max-w-[200px]">
          {t('messaging.startConversation', 'Envoyez un message pour commencer la conversation')}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto flex flex-col-reverse py-4 bg-gradient-to-b from-muted/20 to-transparent"
    >
      {messagesWithSeparators.map((item) => {
        if (item.type === 'separator') {
          return (
            <div key={`separator-${item.date}`} className="flex items-center justify-center my-4 px-4">
              <div className="flex-1 h-px bg-border/50" />
              <span className="px-3 text-[10px] font-medium text-muted-foreground/70 uppercase tracking-wider">
                {formatDateSeparator(item.date)}
              </span>
              <div className="flex-1 h-px bg-border/50" />
            </div>
          );
        }
        return (
          <MessageBubble
            key={item.message.id}
            message={item.message}
            onImageClick={onImageClick}
            onAudioPlay={onAudioPlay}
          />
        );
      })}

      {hasNextPage && (
        <div className="flex justify-center py-3">
          {isLoadingMore ? (
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          ) : (
            <Button variant="outline" size="sm" onClick={onLoadMore} className="h-8 text-xs">
              <ChevronUp className="size-3.5 mr-1.5" />
              {t('messaging.loadMore', 'Charger plus')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
